/** Hub text size steps — matches Settings UI (Compact / Default / Large / Extra Large). */
export type HubTextSize = 'small' | 'medium' | 'large' | 'x_large';

/** JSON stored in the Keycloak `app_theme` user attribute / JWT claim. */
export type AppTheme = {
	fontScale: HubTextSize;
};

export const DEFAULT_HUB_TEXT_SIZE: HubTextSize = 'medium';

export const HUB_TEXT_SIZE_STORAGE_KEY = 'hub-text-size';

export const SCALE_BY_SIZE: Record<HubTextSize, number> = {
	small: 0.875,
	medium: 1,
	large: 1.125,
	'x_large': 1.25,
};

export const HUB_TEXT_SIZE_OPTIONS: ReadonlyArray<{ value: HubTextSize; label: string }> = [
	{ value: 'small', label: 'Compact' },
	{ value: 'medium', label: 'Default' },
	{ value: 'large', label: 'Large' },
	{ value: 'x_large', label: 'Extra Large' },
];

export function isHubTextSize(value: unknown): value is HubTextSize {
	return value === 'small' || value === 'medium' || value === 'large' || value === 'x_large';
}

/** Builds the Keycloak attribute / claim payload. */
export function serializeAppTheme(fontScale: HubTextSize): string {
	const theme: AppTheme = { fontScale };

	return JSON.stringify(theme);
}

/**
 * Parses an `app_theme` JSON string (or claim value).
 * Invalid / missing values fall back to {@link DEFAULT_HUB_TEXT_SIZE}.
 */
export function parseAppTheme(raw: unknown): AppTheme {
	if (typeof raw !== 'string' || raw.trim() === '') {
		return { fontScale: DEFAULT_HUB_TEXT_SIZE };
	}

	try {
		const parsed = JSON.parse(raw) as Partial<AppTheme>;
		if (isHubTextSize(parsed.fontScale)) {
			return { fontScale: parsed.fontScale };
		}
	} catch {
		// Invalid JSON → default
	}

	return { fontScale: DEFAULT_HUB_TEXT_SIZE };
}

/** Reads `app_theme` from a decoded JWT payload or raw access token string. */
export function getFontScaleFromAppThemeClaim(claim: unknown): HubTextSize | undefined {
	if (claim === undefined || claim === null) {
		return undefined;
	}

	// Mapper may emit a string, or occasionally a pre-parsed object.
	if (typeof claim === 'object' && claim !== null && 'fontScale' in claim) {
		const fontScale = (claim as { fontScale?: unknown }).fontScale;
		return isHubTextSize(fontScale) ? fontScale : undefined;
	}

	if (typeof claim === 'string') {
		const theme = parseAppTheme(claim);
		// Distinguish missing claim (empty) from explicit default — callers use
		// presence of a non-empty string as "token has a value".
		if (claim.trim() === '') {
			return undefined;
		}

		return theme.fontScale;
	}

	return undefined;
}
